// Client da API de Analyst Notes (Equity) - servico `miscellaneous`.
//
// Base da URL no mesmo padrao de ./apiBase.ts (fonte unica de verdade de URL
// deste repo), que existe por causa do bug "a aba funciona em dev e quebra no
// deploy": um client na forma relativa e os irmaos na absoluta.
//
// Diferenca deliberada em relacao ao apiBase: la o default em dev e host
// vazio; aqui o default e PRODUCAO em qualquer ambiente. Motivo: trabalho so
// de front nesta tela funciona hoje apontando para producao, e quem faz isso
// depende disso. Subir o backend local passa a ser escolha EXPLICITA, via
// REACT_APP_MISCELLANEOUS_HOST= (vazio) no .env.local - que deixa a URL
// relativa e o setupProxy.js encaminha /miscellaneous/*.

import axios from 'axios'

import type {
  AnalystNote,
  AnalystNoteAnalyst,
  AnalystNoteContext,
  AnalystNoteDraft,
  AnalystNoteDraftPayload,
  AnalystNoteNotifyResult,
  AnalystNoteOptions,
  AnalystNotePayload,
  AnalystNoteRecipient,
  AnalystNotesFeedPage,
  AnalystNotesQuery,
  AnalystNoteSummary,
} from '../../pages/analyst-notes/hooks'
import { QUERY_KEYS, SENTIMENTS } from '../../pages/analyst-notes/hooks'

const PROD_HOST = 'https://api.jgp.com.br'

// '' (string vazia) => URL relativa => setupProxy.js encaminha para o backend
// local. O `??` preserva a string vazia de proposito; nao trocar por `||`.
const host = process.env.REACT_APP_MISCELLANEOUS_HOST ?? PROD_HOST

const BASE = `${host}/miscellaneous/api/v1/analyst_notes`

// O notify e sincrono no backend (um send_email POR destinatario, e so entao
// responde). O default do axios seria curto demais para isso.
const NOTIFY_TIMEOUT_MS = 180_000

// -- Auth ----------------------------------------------------------

const TOKEN_TTL_MS = 5 * 60_000

let tokenCache: { token: string; expiresAt: number } | null = null
let inflightTokenRequest: Promise<string | null> | null = null

const fetchFreshToken = async (): Promise<string | null> => {
  try {
    const res = await fetch('/api/auth/token')
    if (!res.ok) return null
    const { token } = await res.json()
    return typeof token === 'string' ? token : null
  } catch {
    return null
  }
}

const getAuthToken = async (): Promise<string | null> => {
  if (tokenCache && tokenCache.expiresAt > Date.now()) return tokenCache.token
  if (inflightTokenRequest) return inflightTokenRequest

  inflightTokenRequest = fetchFreshToken()
    .then((token) => {
      tokenCache = token
        ? { token, expiresAt: Date.now() + TOKEN_TTL_MS }
        : null
      return token
    })
    .finally(() => {
      inflightTokenRequest = null
    })

  return inflightTokenRequest
}

const getHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = await getAuthToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

// -- Envelope ------------------------------------------------------

// ATENCAO: envelope FLAT. Os campos do payload vem no TOPO da resposta, ao
// lado de message / elapsed_time / count / data_sources / status_flag. NAO
// existe `.data` aqui - o `unwrap` dos outros clients de src/clients/compass
// (que fazem `res.data.data`) devolveria undefined nesta API.
//
// `status_flag` vem SEMPRE "red" nestas rotas e NAO indica erro: o decorator
// que o preenche procura metadados de scraper, e analyst notes nao tem
// nenhum registrado. Ignorado, junto com `data_sources`.
const field = <T>(payload: unknown, key: string, fallback: T): T => {
  const v = (payload as Record<string, unknown> | null | undefined)?.[key]
  return v === undefined || v === null ? fallback : (v as T)
}

// -- Erros ---------------------------------------------------------

/** Erro de API com o `detail` do FastAPI preservado. O 422 de ticker
 *  desconhecido traz uma frase legivel que a tela mostra inline no campo. */
export class AnalystNotesError extends Error {
  readonly status: number | null
  readonly detail: string | null

  constructor(message: string, status: number | null, detail: string | null) {
    super(message)
    this.name = 'AnalystNotesError'
    this.status = status
    this.detail = detail
    // tsconfig tem "target": "es5" - sem isto, `instanceof` numa subclasse de
    // Error sempre da false e o tratamento por status na tela nao funciona.
    Object.setPrototypeOf(this, AnalystNotesError.prototype)
  }
}

// `detail` e string (erro de negocio) ou array de {loc,msg,type} (validacao
// do pydantic). Normaliza para uma frase.
const readDetail = (raw: unknown): string | null => {
  if (typeof raw === 'string') return raw.trim() || null
  if (Array.isArray(raw)) {
    const msgs = raw
      .map((i) => (i as { msg?: unknown } | null)?.msg)
      .filter((m): m is string => typeof m === 'string')
    return msgs.length ? msgs.join('; ') : null
  }
  if (raw && typeof raw === 'object') return JSON.stringify(raw)
  return null
}

// Duck-typing em vez de axios.isAxiosError: o axios esta pinado em ^0.21 e
// isto sobrevive a um bump sem surpresa.
const toError = (e: unknown, fallback: string): AnalystNotesError => {
  if (e instanceof AnalystNotesError) return e
  const res = (
    e as { response?: { status?: number; data?: { detail?: unknown } } } | null
  )?.response
  const detail = readDetail(res?.data?.detail)
  return new AnalystNotesError(detail ?? fallback, res?.status ?? null, detail)
}

// -- Query params --------------------------------------------------

// Omite qualquer chave vazia/whitespace: mandar `sector=` filtraria pela
// string vazia e voltaria lista vazia. Por isso o filtro de nota sem
// sentimento e o sentinela NO_SENTIMENT ("Sem sentimento"), nunca "".
//
// NUNCA normalize o case aqui. `sector`, `sentiment` e `note_type` sao
// case-sensitive no servidor e chegam verbatim de GET /options; `ticker` e
// case-insensitive la, entao tambem passa como veio.
const buildParams = (q: AnalystNotesQuery = {}): Record<string, string> => {
  const params: Record<string, string> = {}
  QUERY_KEYS.forEach((k) => {
    const v = q[k]
    if (typeof v === 'string' && v.trim() !== '') params[k] = v
  })
  return params
}

// `tickers` vai REPETIDO (`?tickers=A&tickers=B`), que e o que o FastAPI le
// como lista. Por isso URLSearchParams: o axios 0.21 serializaria o array
// como `tickers[]=A`, que o servidor ignora.
const buildFeedParams = (
  q: AnalystNotesQuery,
  page: { limit: number; cursor: string | null }
): URLSearchParams => {
  const params = new URLSearchParams(buildParams(q))
  const tickers = q.tickers ?? []
  tickers.forEach((t) => {
    if (t.trim() !== '') params.append('tickers', t)
  })
  params.set('limit', String(page.limit))
  if (page.cursor) params.set('cursor', page.cursor)
  return params
}

// -- Notas ---------------------------------------------------------

/** Uma pagina do feed: RESUMOS, sem `summary` / `comment` - a nota completa
 *  vem de fetchAnalystNote. Ordem `date` desc, `_id` desc; `next_cursor` e
 *  keyset nessa ordem, entao uma nota publicada no meio da rolagem nao
 *  desloca as paginas seguintes. */
export const fetchAnalystNoteFeed = async (
  q: AnalystNotesQuery,
  page: { limit: number; cursor: string | null }
): Promise<AnalystNotesFeedPage> => {
  try {
    const headers = await getHeaders()
    const { data } = await axios.get(`${BASE}/notes/feed`, {
      headers,
      params: buildFeedParams(q, page),
    })
    const notes = field<AnalystNoteSummary[]>(data, 'notes', [])
    const list = Array.isArray(notes) ? notes : []
    return {
      notes: list,
      count: field<number>(data, 'count', list.length),
      next_cursor: field<string | null>(data, 'next_cursor', null),
    }
  } catch (e) {
    throw toError(e, 'Could not load the notes.')
  }
}

/** Nota completa, com o conteudo. `null` quando ela nao existe OU o usuario
 *  nao pode le-la - o servidor responde 404 nos dois casos, de proposito. Os
 *  demais erros lancam. O id pode vir da URL (`?note=`), por isso codificado. */
export const fetchAnalystNote = async (
  noteId: string
): Promise<AnalystNote | null> => {
  try {
    const headers = await getHeaders()
    const { data } = await axios.get(
      `${BASE}/notes/${encodeURIComponent(noteId)}`,
      { headers }
    )
    return field<AnalystNote | null>(data, 'note', null)
  } catch (e) {
    const err = toError(e, 'Could not load the note.')
    if (err.status === 404) return null
    throw err
  }
}

export const createAnalystNote = async (
  payload: AnalystNotePayload
): Promise<AnalystNote> => {
  try {
    const headers = await getHeaders()
    const { data } = await axios.post(`${BASE}/notes`, payload, { headers })
    return field<AnalystNote | null>(data, 'note', null) as AnalystNote
  } catch (e) {
    throw toError(e, 'Could not publish the note.')
  }
}

/** PUT e SUBSTITUICAO TOTAL, nao patch: campos omitidos viram "" no servidor.
 *  Mande sempre o payload completo. Excecoes: `broker` + `rating` ausentes
 *  sao MANTIDOS (aqui vao sempre, e sempre juntos), e `context` e imutavel -
 *  fica fora do corpo, para a edicao nao depender de o servidor ignora-lo.
 *
 *  Preservados pelo backend: `notified`, `created_by`, `created_at`. Ou seja,
 *  editar uma nota ja enviada NAO a "desenvia". Nota que o usuario nao pode
 *  ler e 404, igual a nota inexistente. */
export const updateAnalystNote = async (
  noteId: string,
  payload: AnalystNotePayload
): Promise<AnalystNote> => {
  const { context, ...body } = payload
  try {
    const headers = await getHeaders()
    const { data } = await axios.put(`${BASE}/notes/${noteId}`, body, {
      headers,
    })
    return field<AnalystNote | null>(data, 'note', null) as AnalystNote
  } catch (e) {
    throw toError(e, 'Could not save the note.')
  }
}

/** Hard delete, sem undo no backend. Confirme antes de chamar. */
export const deleteAnalystNote = async (noteId: string): Promise<void> => {
  try {
    const headers = await getHeaders()
    await axios.delete(`${BASE}/notes/${noteId}`, { headers })
  } catch (e) {
    throw toError(e, 'Could not delete the note.')
  }
}

/** Envia a nota por e-mail para os destinatarios escolhidos e marca a nota
 *  como notificada.
 *
 *  Quatro coisas que a UI precisa respeitar:
 *   - lista vazia e 422: nao existe fallback para uma lista de alerta;
 *   - e-mail fora de `GET /recipients` e 400;
 *   - a falha e PARCIAL - um envio por destinatario, entao `failed` pode vir
 *     preenchido com `result: true`;
 *   - NAO e idempotente - chamar duas vezes envia duas vezes. */
export const notifyAnalystNote = async (
  noteId: string,
  recipients: string[]
): Promise<AnalystNoteNotifyResult> => {
  try {
    const headers = await getHeaders()
    const { data } = await axios.post(
      `${BASE}/notes/${noteId}/notify`,
      { recipients },
      { headers, timeout: NOTIFY_TIMEOUT_MS }
    )
    return {
      result: field<boolean>(data, 'result', false),
      sent: field<string[]>(data, 'sent', []),
      failed: field<string[]>(data, 'failed', []),
    }
  } catch (e) {
    throw toError(e, 'Could not send the notification.')
  }
}

// -- Opcoes (cache de sessao) --------------------------------------

// Endpoint mais lento do conjunto (varre a colecao de classificacao). O
// contrato manda buscar uma vez e cachear pela sessao - por isso o cache vive
// no modulo, e nao num hook: sobrevive a desmontagem da tela e a reabertura
// do drawer. Sem TTL, de proposito. A resposta e POR USUARIO (contextos e
// membros), entao o logout zera o cache - ver clearAnalystNotesCaches.
let optionsCache: AnalystNoteOptions | null = null
let inflightOptions: Promise<AnalystNoteOptions> | null = null

const requestOptions = async (): Promise<AnalystNoteOptions> => {
  const headers = await getHeaders()
  const { data } = await axios.get(`${BASE}/options`, { headers })
  const sentiments = field<string[]>(data, 'sentiments', [])
  return {
    tickers: field<string[]>(data, 'tickers', []),
    sectors: field<string[]>(data, 'sectors', []),
    note_types: field<string[]>(data, 'note_types', []),
    // Fallback defensivo: o segmented control nao pode ficar sem opcao.
    sentiments: sentiments.length ? sentiments : [...SENTIMENTS],
    analysts: field<AnalystNoteAnalyst[]>(data, 'analysts', []).filter(
      (a) => !!a?.email
    ),
    contexts: field<AnalystNoteContext[]>(data, 'contexts', []).filter(
      (c) => !!c?.key
    ),
    brokers: field<string[]>(data, 'brokers', []),
    ratings: field<number[]>(data, 'ratings', []),
  }
}

export const fetchAnalystNoteOptions = (
  force = false
): Promise<AnalystNoteOptions> => {
  if (!force && optionsCache) return Promise.resolve(optionsCache)
  if (inflightOptions) return inflightOptions

  inflightOptions = requestOptions()
    .then((opts) => {
      optionsCache = opts
      return opts
    })
    .catch((e) => {
      throw toError(e, 'Could not load the form options.')
    })
    .finally(() => {
      inflightOptions = null
    })

  return inflightOptions
}

/** Injeta um note_type recem-criado no cache.
 *
 *  `note_types` e descoberto das notas existentes, entao um tipo novo so
 *  apareceria no /options depois que a nota fosse salva. Sem isto, o tipo que
 *  o usuario acabou de criar sumiria do combobox ate um refetch do endpoint
 *  mais caro da API. */
export const primeNoteType = (noteType: string): void => {
  const t = noteType.trim()
  if (!t || !optionsCache) return
  if (optionsCache.note_types.some((x) => x === t)) return
  optionsCache = {
    ...optionsCache,
    note_types: [...optionsCache.note_types, t].sort((a, b) =>
      a.localeCompare(b)
    ),
  }
}

export const invalidateAnalystNoteOptions = (): void => {
  optionsCache = null
}

// -- Destinatarios (cache de sessao) -------------------------------

// Mesma logica do cache de /options: a colecao e semeada a mao e muda em
// escala de meses. Alem do seletor de envio, e daqui que sai o NOME REAL do
// autor de cada nota - a tela pede a lista uma vez e resolve todos.
let recipientsCache: AnalystNoteRecipient[] | null = null
let inflightRecipients: Promise<AnalystNoteRecipient[]> | null = null

const requestRecipients = async (): Promise<AnalystNoteRecipient[]> => {
  const headers = await getHeaders()
  const { data } = await axios.get(`${BASE}/recipients`, { headers })
  const list = field<AnalystNoteRecipient[]>(data, 'recipients', [])
  return (Array.isArray(list) ? list : []).filter((r) => !!r?.email)
}

export const fetchAnalystNoteRecipients = (
  force = false
): Promise<AnalystNoteRecipient[]> => {
  if (!force && recipientsCache) return Promise.resolve(recipientsCache)
  if (inflightRecipients) return inflightRecipients

  inflightRecipients = requestRecipients()
    .then((list) => {
      recipientsCache = list
      return list
    })
    .catch((e) => {
      throw toError(e, 'Could not load the recipients.')
    })
    .finally(() => {
      inflightRecipients = null
    })

  return inflightRecipients
}

/** Zera token, /options e /recipients. Chamado no logout: os caches sao do
 *  modulo (por aba, nao por usuario) e o logout nao recarrega a pagina - sem
 *  isto, o proximo login na mesma aba herdaria o token de ate 5 minutos e os
 *  contextos do usuario anterior. */
export const clearAnalystNotesCaches = (): void => {
  tokenCache = null
  optionsCache = null
  recipientsCache = null
}

// -- Rascunhos -----------------------------------------------------

export const fetchAnalystNoteDrafts = async (): Promise<AnalystNoteDraft[]> => {
  try {
    const headers = await getHeaders()
    const { data } = await axios.get(`${BASE}/drafts`, { headers })
    const drafts = field<AnalystNoteDraft[]>(data, 'drafts', [])
    return Array.isArray(drafts) ? drafts : []
  } catch (e) {
    throw toError(e, 'Could not load your drafts.')
  }
}

/** Upsert. `draftId` precisa ser um UUID valido (400 caso contrario) e e
 *  gravado VERBATIM como _id: use sempre a forma canonica minuscula com
 *  hifens, senao a forma sem hifens cria um SEGUNDO documento. */
export const upsertAnalystNoteDraft = async (
  draftId: string,
  payload: AnalystNoteDraftPayload
): Promise<AnalystNoteDraft> => {
  try {
    const headers = await getHeaders()
    const { data } = await axios.put(`${BASE}/drafts/${draftId}`, payload, {
      headers,
    })
    return field<AnalystNoteDraft | null>(
      data,
      'draft',
      null
    ) as AnalystNoteDraft
  } catch (e) {
    throw toError(e, 'Could not save the draft.')
  }
}

export const deleteAnalystNoteDraft = async (
  draftId: string
): Promise<void> => {
  try {
    const headers = await getHeaders()
    await axios.delete(`${BASE}/drafts/${draftId}`, { headers })
  } catch (e) {
    throw toError(e, 'Could not delete the draft.')
  }
}

/** Fecha a janela entre "publiquei" e "apaguei o rascunho": se o DELETE
 *  falhar por rede, o rascunho ficaria orfao no painel para sempre. Retenta
 *  em escada curta. Fire-and-forget - nunca deve segurar o fechamento do
 *  editor. */
export const deleteAnalystNoteDraftWithRetry = async (
  draftId: string
): Promise<void> => {
  const delays = [200, 600, 1500]
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      await deleteAnalystNoteDraft(draftId)
      return
    } catch (e) {
      // 404 = ja nao existe. Retentar seria so gastar tres requests.
      if (e instanceof AnalystNotesError && e.status === 404) return
      if (attempt === delays.length) return
      await new Promise((resolve) => setTimeout(resolve, delays[attempt]))
    }
  }
}
